import * as React from 'react'
import { useQuery } from '@tanstack/react-query'
import { MINUTE_IN_MS } from '@/modules/shared/constants/time'
import { addDays, format } from 'date-fns'
import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { useParams } from 'react-router-dom'
import { useDate } from '@/modules/drivers/stores/dateStore'
import { useDrivers } from '@/modules/shared/context/drivers/DriversContext'
import useViolationService from '@/modules/drivers/services/ViolationService'
import type { GroupedViolationModel } from '@/app/models/groupedViolationModel'
import type { ViolationModel } from '@/app/models/violationModel'
import { zustandPlugin } from '@/modules/shared/openreplay'

const EMPTY_VIOLATIONS: GroupedViolationModel[] = []

type SearchState = {
  search: string
  setSearch: (next: string) => void
}

export const useViolationsSearchStore = create<SearchState>()(
  devtools(
    zustandPlugin((set) => ({
      search: '',
      setSearch: (search) => set({ search }, false, 'setSearch'),
    }), 'violations-search'),
    { name: 'EasyTacho', store: 'violations-search' },
  ),
)

type UseViolationsResult = {
  violations: GroupedViolationModel[]
  filteredViolations: GroupedViolationModel[]
  search: string
  setSearch: (next: string) => void
  searchResults: number
  isLoading: boolean
  getViolations: (params: { driverId: number }) => Promise<ViolationModel[]>
}

export const useViolations = (): UseViolationsResult => {
  const { companyId } = useParams<{ companyId: string }>()
  const search = useViolationsSearchStore((s) => s.search)
  const setSearch = useViolationsSearchStore((s) => s.setSearch)
  const { range, formatApiDate } = useDate()
  const { drivers, selectedIds } = useDrivers()
  const { getGroupedViolations, getViolations: getViolationsService } =
    useViolationService()

  const driverIdsToFetch = React.useMemo(() => {
    if (selectedIds && selectedIds.length > 0) {
      return selectedIds.map((id) => parseInt(id, 10))
    }
    return drivers.map((d) => d.id)
  }, [selectedIds, drivers])

  const canFetch =
    !!companyId &&
    !!range?.startDate &&
    !!range?.endDate &&
    driverIdsToFetch.length > 0

  const queryKey = React.useMemo(
    () => [
      'groupedViolations',
      companyId,
      range?.startDate?.fullDate,
      range?.endDate?.fullDate,
      driverIdsToFetch,
    ],
    [companyId, range, driverIdsToFetch],
  )

  const { data: violations = EMPTY_VIOLATIONS, isFetching } = useQuery<
    GroupedViolationModel[]
  >({
    queryKey,
    enabled: canFetch,
    staleTime: MINUTE_IN_MS * 5,
    queryFn: async () => {
      if (!range?.startDate || !range?.endDate) return []
      const startDate = new Date(
        range.startDate.year!,
        range.startDate.month! - 1,
        range.startDate.day!,
      )
      const endDate = new Date(
        range.endDate.year!,
        range.endDate.month! - 1,
        range.endDate.day!,
      )
      return getGroupedViolations({
        companyId,
        drivers: driverIdsToFetch,
        startTime: startDate,
        endTime: addDays(endDate, 1),
      })
    },
  })

  const filteredViolations = React.useMemo(() => {
    if (!search || search.trim() === '') return violations
    const q = search.toLowerCase().trim()
    return violations.filter((violation) => {
      const driver =
        violation.driver ||
        (violation.driverId
          ? drivers.find((d) => d.id === violation.driverId)
          : null)
      const fullName = driver
        ? `${driver.surname || ''} ${driver.name || ''}`.toLowerCase()
        : ''
      return fullName.includes(q)
    })
  }, [violations, search, drivers])

  const getViolations = React.useCallback(
    async ({ driverId }: { driverId: number }): Promise<ViolationModel[]> => {
      if (!range?.startDate || !range?.endDate) return []
      const startDate = new Date(
        range.startDate.year!,
        range.startDate.month! - 1,
        range.startDate.day!,
      )
      const endDate = new Date(
        range.endDate.year!,
        range.endDate.month! - 1,
        range.endDate.day!,
      )
      const startTime = formatApiDate(startDate)
      const endTime = encodeURIComponent(
        `${format(endDate, 'YYYY-MM-DD')} 23:59`,
      )
      return getViolationsService({
        companyId,
        driverId,
        startTime,
        endTime,
      })
    },
    [companyId, range, formatApiDate, getViolationsService],
  )

  return {
    violations,
    filteredViolations,
    search,
    setSearch,
    searchResults: filteredViolations.length,
    isLoading: isFetching,
    getViolations,
  }
}
